const CEEB_CODE_SEPARATOR_REGEX = /[,\s]+/;
const TRAILING_ZERO_DECIMAL_REGEX = /\.0$/;
const DIGITS_ONLY_REGEX = /^\d+$/;

const stripTrailingZeroDecimal = (token: string): string =>
  token.replace(TRAILING_ZERO_DECIMAL_REGEX, '');

/**
 * Splits pasted/typed text into format-valid CEEB code tokens (FR-5–FR-11):
 * comma/space/tab/newline runs separate tokens, a trailing ".0" spreadsheet
 * artifact is stripped before the digits-only check, and anything left that
 * isn't all digits (or empty) is silently dropped rather than becoming a chip.
 */
export const parseCeebCodeTokens = (input: string): string[] =>
  input
    .split(CEEB_CODE_SEPARATOR_REGEX)
    .map((token) => stripTrailingZeroDecimal(token.trim()))
    .filter((token) => DIGITS_ONLY_REGEX.test(token));

/** Splits a persisted comma-joined `EligibilityCriteriaDto.values` string back into codes. */
export const splitStoredCeebCodes = (values: string | undefined): string[] =>
  (values ?? '')
    .split(',')
    .map((token) => stripTrailingZeroDecimal(token.trim()))
    .filter(Boolean);

export const CEEB_ALL_UNMATCHED_MESSAGE =
  'No codes have admitted student records';

/** FR-23/FR-25: exact wording, do not paraphrase. */
export const getCeebPartialUnmatchedMessage = (
  unmatchedCount: number,
  totalCount: number
): string =>
  `${unmatchedCount} of ${totalCount} codes have no admitted student records`;

/**
 * The warning message for a resolved CEEB batch, or `null` when every code
 * matched (FR-22/FR-23/FR-24). Callers gate "still pending" separately.
 */
export const getCeebUnmatchedMessage = (
  matchedCount: number,
  totalCount: number
): string | null => {
  if (!totalCount || matchedCount === totalCount) {
    return null;
  }

  return matchedCount === 0
    ? CEEB_ALL_UNMATCHED_MESSAGE
    : getCeebPartialUnmatchedMessage(totalCount - matchedCount, totalCount);
};
